import { useEffect, useState } from "react";
import { FiBook, FiCheck, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import {assignSubjectsToStudent, deleteSubjectFromStudent, getStudentSubjectAssignments} from "../../../services/studentsApi";
import { getSubjects } from "../../../services/subjectsApi";
import Select from "../../../components/common/select/Select";
import styles from "./StudentSubjectsModal.module.css";

const StudentSubjectsModal = ({ isOpen, student, onClose, onAssigned }) => {
  const { t } = useTranslation();
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setSelectedSubjectId("");
      setSelectedTopics([]);
      setError("");
      return;
    }

    const fetchSubjects = async () => {
      try {
        setLoadingSubjects(true);
        const data = await getStudentSubjectAssignments(student.studentNumber);
        const content = data.content || data || [];
        setSubjects(content);
      } catch (err) {
        setError(t("pages.studentSubjects.loadError"));
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchSubjects();
  }, [isOpen]);

  if (!isOpen || !student) return null;

  const parentSubjects = subjects.filter((subject) => subject.parentId === null || subject.parentId === undefined);

  // Build the topic list for a given parent subject: its direct topics plus any child subjects that
  // themselves act as leaf topics. `assigned` reflects the student's CURRENT active assignment.
  const computeTopics = (subjectIdValue) => {
    const parent = parentSubjects.find((subject) => String(subject.id) === String(subjectIdValue)) || null;
    if (!parent) return [];
    const directTopics = Array.isArray(parent.topics) ? parent.topics : [];
    const childSubjectsAsTopics = subjects.filter(
      (subject) => subject.parentId === parent.id && (!subject.topics || subject.topics.length === 0)
    );
    const map = new Map();
    [...directTopics, ...childSubjectsAsTopics].forEach((topic) => {
      if (!map.has(topic.id)) {
        map.set(topic.id, {
          id: topic.id,
          name: topic.name || topic.title || topic.subjectTitle || `ID: ${topic.id}`,
          assigned: topic.assigned,
        });
      }
    });
    return Array.from(map.values());
  };

  const topics = computeTopics(selectedSubjectId);

  // Topics currently assigned (active) to the student within the selected subject, and whether the
  // user's selection differs from it (added and/or removed) — used to enable Save.
  const assignedTopicIds = topics.filter((topic) => topic.assigned).map((topic) => topic.id);
  const hasChanges = Boolean(selectedSubjectId) && (
    selectedTopics.some((id) => !assignedTopicIds.includes(id)) ||
    assignedTopicIds.some((id) => !selectedTopics.includes(id))
  );

  const handleSubjectChange = (e) => {
    const newId = e.target.value;
    setSelectedSubjectId(newId);
    // Pre-check the topics already assigned to the student so they render checked and can be
    // toggled OFF to unassign them.
    setSelectedTopics(computeTopics(newId).filter((topic) => topic.assigned).map((topic) => topic.id));
    setError("");
  };

  const toggleTopic = (topicId) => {
    const id = Number(topicId);
    setSelectedTopics((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!selectedSubjectId) {
      setError(t("pages.studentSubjects.subjectRequired"));
      return;
    }

    // Diff the desired selection against what's already assigned (within THIS subject's topics only,
    // so other subjects are never touched): add the newly-checked topics, unassign the unchecked ones.
    const added = selectedTopics.filter((id) => !assignedTopicIds.includes(id));
    const removed = assignedTopicIds.filter((id) => !selectedTopics.includes(id));

    if (added.length === 0 && removed.length === 0) {
      return;
    }

    try {
      setSaving(true);

      if (added.length > 0) {
        await assignSubjectsToStudent(student.studentNumber, added);
      }
      for (const topicId of removed) {
        await deleteSubjectFromStudent(student.studentNumber, topicId);
      }

      if (onAssigned) await onAssigned();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err.message || t("pages.studentSubjects.assignError");

      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const studentName = student.firstName && student.lastName ? `${student.firstName} ${student.lastName}` : student.firstName || student.lastName || t("pages.studentSubjects.studentFallback");

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="student-subjects-modal-title">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.titleWrap}>
            <span className={styles.headerIcon}>
              <FiBook size={22} />
            </span>
            <div className={styles.titleBlock}>
              <h2 id="student-subjects-modal-title" className={styles.title}>
                {t("pages.studentSubjects.title")}
              </h2>
              {studentName && <p className={styles.subtitle}>{t("pages.studentSubjects.studentLabel", { name: studentName })}</p>}
            </div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t("pages.studentSubjects.closeAria")} disabled={saving}>
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formBody}>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="subject-select">{t("pages.studentSubjects.selectSubject")}</label>
              <Select
                id="subject-select"
                className={styles.select}
                value={selectedSubjectId}
                onChange={handleSubjectChange}
                disabled={loadingSubjects || saving}
                options={[
                  {
                    value: "",
                    label: loadingSubjects
                      ? t("pages.studentSubjects.subjectsLoading")
                      : t("pages.studentSubjects.subjectPlaceholder"),
                  },
                  ...parentSubjects.map((subject) => ({
                    value: subject.id,
                    label: subject.name || subject.subjectTitle || subject.title || `ID: ${subject.id}`,
                  })),
                ]}
              />
            </div>

            <div className={styles.formGroup}>
              <div className={styles.labelRow}>
                <label className={styles.label}>{t("pages.studentSubjects.selectTopic")}</label>
                {selectedSubjectId && topics.length > 0 && (
                  <span className={styles.topicCount}>
                    {selectedTopics.length} / {topics.length}
                  </span>
                )}
              </div>
              {selectedTopics.length > 0 && (
                <div className={styles.selectedChips}>
                  {selectedTopics.map((topicId) => {
                    const topic = topics.find((t) => t.id === topicId);
                    return topic ? (
                      <span key={topic.id} className={styles.chip}>
                        <FiCheck size={14} className={styles.chipIcon} />
                        {topic.name}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
              <div
                className={styles.topicCheckboxList}
                role="group"
                aria-label={t("pages.studentSubjects.selectTopicsAria")}
              >
                {!selectedSubjectId && (
                  <div className={styles.topicEmpty}>
                    <FiBook size={28} className={styles.topicEmptyIcon} />
                    <p className={styles.topicPlaceholder}>{t("pages.studentSubjects.selectSubjectFirst")}</p>
                  </div>
                )}
                {selectedSubjectId && topics.length === 0 && (
                  <div className={styles.topicEmpty}>
                    <p className={styles.topicPlaceholder}>{t("pages.studentSubjects.noTopics")}</p>
                  </div>
                )}
                {selectedSubjectId &&
                  topics.length > 0 &&
                  topics.map((topic, index) => {
                    const isSelected = selectedTopics.includes(topic.id);
                    return (
                      <label
                        key={topic.id}
                        className={`${styles.topicItem} ${isSelected ? styles.topicItemSelected : ""}`}
                        style={{ animationDelay: `${index * 30}ms` }}
                      >
                        <span className={styles.topicCheckboxWrap}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleTopic(topic.id)}
                            disabled={loadingSubjects || saving}
                            className={styles.topicCheckbox}
                          />
                          {isSelected && (
                            <FiCheck size={14} className={styles.topicCheckmark} />
                          )}
                        </span>
                        <span className={styles.topicName}>{topic.name}</span>
                        {topic.assigned && (
                          <span className={styles.assignedBadge}>{t("pages.studentSubjects.alreadyAssigned")}</span>
                        )}
                      </label>
                    );
                  })}
              </div>
            </div>

            {error && (
              <div className={styles.error} role="alert">
                {error}
              </div>
            )}
          </div>

          <div className={styles.actions}>
            <button type="button" onClick={onClose} className={styles.secondaryBtn} disabled={saving}>
              {t("common.close")}
            </button>
            <button type="submit" className={styles.primaryBtn} disabled={saving || loadingSubjects || !hasChanges}>
              {saving ? t("pages.studentSubjects.saving") : t("pages.studentSubjects.assign")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentSubjectsModal;
